import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Container, 
  CircularProgress, 
  Alert,
  AlertTitle,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

import RecruitmentNavigation from './RecruitmentNavigation';
import Step1DataPribadi from './Step1DataPribadi';
import Step2Pendidikan from './Step2Pendidikan';
import Step3PengalamanKerja from './Step3PengalamanKerja';
import Step4Bahasa from './Step4Bahasa';
import Step5Gaji from './Step5Gaji';
import Step6PertanyaanTambahan from './Step6PertanyaanTambahan';
import Step7Review from './Step7Review';
import Step8Dokumen from './Step8Dokumen';

const getInitialFormState = () => ({
  // Data Pribadi
  fullName: '',
  nickname: '',
  birthPlace: '',
  birthDate: '',
  nationality: '',
  religion: '',
  maritalStatus: '',
  gender: '',
  bloodType: '',
  colorBlind: '',
  heightCm: '',
  weightKg: '',
  addressKtp: '',
  addressCurrent: '',
  phone: '',
  email: '',
  
  // Data Pendidikan
  education: [{ period: '', schoolName: '', major: '', grade: '', city: '', certificate: '' }],
  lastEducation: '',
  educationMajor: '',
  educationInstitution: '',
  educationGraduationYear: '',
  
  // Data Pengalaman Kerja
  workHistory: [{ companyName: '', period: '', position: '', basicSalary: '', allowance: '', movingReason: '', jobDescription: '' }],
  hobby: '',
  selfPositivePoints: '',
  selfWeaknessPoints: '',
  innovationDescription: '',
  reference1Name: '',
  reference1Position: '',
  reference1Phone: '',
  reference1Email: '',
  reference2Name: '',
  reference2Position: '',
  reference2Phone: '',
  reference2Email: '',
  award1: '',
  award2: '',
  award3: '',
  expectedSalary: '',
  expectedBenefits: '',
  mainSkills: '',
  supportingSkills: '',
  certifications: '',
  experienceSummary: '',
  lastCompany: '',
  portfolioUrl: '',
  
  // Data Bahasa
  languages: [{ language: '', written: '', oral: '', remark: '' }],
  primaryLanguage: '',
  strongestForeignLanguage: '',
  foreignLanguageExperience: '',
  languageCertifications: '',
  presentationSkills: '',
  negotiationSkills: '',
  writingSkills: '',
  
  // Data Gaji
  salaryPeriod: '',
  currency: '',
  minimumSalary: '',
  idealSalary: '',
  benefitsWanted: '',
  healthInsurance: '',
  transportAllowance: '',
  mealAllowance: '',
  bonusIncentives: '',
  workType: '',
  workLocation: '',
  availabilityDate: '',
  preferredWorkingHours: '',
  salaryNotes: '',
  salaryNegotiation: '',
  
  // Pertanyaan Tambahan
  allowContactInstitution: '',
  hasRelativesInCompany: '',
  relativesInfo: '',
  hasPhysicalDisabilities: '',
  disabilitiesInfo: '',
  hasSeriousIllness: '',
  illnessInfo: '',
  planContinueEducation: '',
  educationPlanInfo: '',
  whyJoinCompany: '',
  willingWorkBeyondExpertise: '',
  beyondExpertiseReason: '',
  whenCanStart: '',
  companyKnowledge: '',
  additionalInfo: '',
  jobSource: '',
  positionExpectations: '',
  
  // Data Organisasi
  organization1Name: '',
  organization1Period: '',
  organization1Position: '',
  organization2Name: '',
  organization2Period: '',
  organization2Position: '',
  organization3Name: '',
  organization3Period: '',
  organization3Position: '',
  
  // Data Keluarga
  familyData: [
    { relation: '', age: '', education: '', job: '' }, // Ayah Kandung (index 0)
    { relation: '', age: '', education: '', job: '' }, // Ibu Kandung (index 1)
    { relation: '', age: '', education: '', job: '' }  // Suami/Istri (index 2) - static
  ],
  siblings: [], // Saudara kandung tambahan (dynamic)
  children: [], // Anak-anak (dynamic)
  
  // Data Dokumen
  documents: [],
});

const RecruitmentForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(() => getInitialFormState());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    message: ''
  });
  
  // Debug: Monitor form state changes
  useEffect(() => {
    console.log('Form state changed, workHistory:', form.workHistory);
  }, [form.workHistory]);
  
  // Debug: Log setiap render
  console.log('RecruitmentForm rendering, activeStep:', activeStep, 'workHistory:', form.workHistory);

  const steps = [
    'Data Pribadi',
    'Pendidikan',
    'Pengalaman Kerja',
    'Bahasa',
    'Gaji & Tunjangan',
    'Pertanyaan Tambahan',
    'Upload Dokumen',
    'Review Data'
  ];

  const closeDuplicateDialog = () => {
    setDuplicateDialog({ open: false, message: '' });
  };

  const handleDuplicateDialogClose = (_, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    closeDuplicateDialog();
  };

  const handleNext = () => {
    console.log('handleNext called, activeStep:', activeStep);
    console.log('Current step name:', steps[activeStep]);
    
    // Validasi step sebelum pindah ke step berikutnya
    const validation = getStepValidation(activeStep);
    console.log('Validation result:', validation);
    if (!validation.isValid) {
      setError(validation.message);
      // Scroll ke atas dan highlight error
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        highlightErrorFields();
      }, 100);
      return;
    }
    
    // Clear error jika validasi berhasil
    setError('');
    
    // Hapus semua error field saat navigasi berhasil
    document.querySelectorAll('.error-field').forEach(el => {
      el.classList.remove('error-field');
    });
    
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    console.log('handleBack called, activeStep:', activeStep);
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      setError(''); // Clear error saat kembali ke step sebelumnya
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepClick = (step) => {
    setActiveStep(step);
    setError(''); // Clear error saat step diklik
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEdit = (step) => {
    setError(''); // Clear error saat edit
    setActiveStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    // Validasi semua step sebelum submit dengan pesan detail
    for (let i = 0; i < steps.length - 1; i++) {
      const validation = getStepValidation(i);
      if (!validation.isValid) {
        // Pindah ke step yang error
        setActiveStep(i);
        setError(`❌ Tidak dapat mengirim pendaftaran!\n\nStep ${i + 1} (${steps[i]}) belum lengkap:\n\n${validation.message.replace('⚠️ ', '')}`);
        setLoading(false);
        // Scroll ke atas dan highlight error
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          highlightErrorFields();
        }, 100);
        return;
      }
    }

    try {
      // Hapus semua error field sebelum submit
      document.querySelectorAll('.error-field').forEach(el => {
        el.classList.remove('error-field');
      });
      
      // 1) Create application (JSON, exclude documents)
      const { createApplication, uploadRecruitmentDocument } = await import('../../services/recruitmentService');
      const { documents = [], ...payload } = form || {};
      
      // Clean numeric fields by removing thousand separators before sending to backend
      const cleanPayload = {
        ...payload,
        expectedSalary: payload.expectedSalary ? payload.expectedSalary.replace(/\./g, '') : '',
        minimumSalary: payload.minimumSalary ? payload.minimumSalary.replace(/\./g, '') : '',
        idealSalary: payload.idealSalary ? payload.idealSalary.replace(/\./g, '') : '',
        // Clean work history salary fields
        workHistory: payload.workHistory ? payload.workHistory.map(work => ({
          ...work,
          basicSalary: work.basicSalary ? work.basicSalary.replace(/\./g, '') : '',
          allowance: work.allowance ? (() => {
            // Parse allowance: jika mengandung titik, hapus titik untuk angka, tapi biarkan text
            const allowanceStr = work.allowance.toString();
            // Cek apakah ada angka yang diformat dengan titik
            const formattedNumbers = allowanceStr.match(/\d+(\.\d+)*/g);
            if (formattedNumbers && formattedNumbers.length > 0) {
              let parsedAllowance = allowanceStr;
              formattedNumbers.forEach(formattedNum => {
                const parsedNum = formattedNum.replace(/\./g, '');
                if (formattedNum !== parsedNum) {
                  parsedAllowance = parsedAllowance.split(formattedNum).join(parsedNum);
                }
              });
              return parsedAllowance;
            }
            return allowanceStr;
          })() : '',
          jobDescription: work.jobDescription || ''
        })) : []
      };
      
      // Debug: Log organisasi data before sending
      console.log('=== DEBUG ORGANISASI DATA ===');
      console.log('organization1Name:', cleanPayload.organization1Name);
      console.log('organization1Period:', cleanPayload.organization1Period);
      console.log('organization1Position:', cleanPayload.organization1Position);
      console.log('organization2Name:', cleanPayload.organization2Name);
      console.log('organization2Period:', cleanPayload.organization2Period);
      console.log('organization2Position:', cleanPayload.organization2Position);
      console.log('organization3Name:', cleanPayload.organization3Name);
      console.log('organization3Period:', cleanPayload.organization3Period);
      console.log('organization3Position:', cleanPayload.organization3Position);
      console.log('=== END DEBUG ===');
      
      // Debug: Log workHistory and jobDescription data before sending
      console.log('=== DEBUG WORKHISTORY DATA ===');
      console.log('workHistory:', cleanPayload.workHistory);
      console.log('workHistory length:', cleanPayload.workHistory?.length || 0);
      if (cleanPayload.workHistory && cleanPayload.workHistory.length > 0) {
        cleanPayload.workHistory.forEach((work, idx) => {
          console.log(`Work ${idx + 1}:`, {
            companyName: work.companyName,
            period: work.period,
            position: work.position,
            jobDescription: work.jobDescription
          });
        });
      }
      console.log('=== END WORKHISTORY DEBUG ===');
      
      const { id: applicationId } = await createApplication(cleanPayload);

      // 2) Upload documents sequentially (can be parallelized if needed)
      for (const doc of documents) {
        if (!doc?.file) continue;
        await uploadRecruitmentDocument(applicationId, doc.file, doc.type || 'additional');
      }

      setSuccess(true);

      // Reset form after successful submission
      setTimeout(() => {
        setForm(getInitialFormState());
        setActiveStep(0);
        setSuccess(false);
      }, 2000);
    } catch (err) {
      let msg = err?.response?.data?.message || err.message || 'Gagal mengirim data. Silakan coba lagi.';
      const isDuplicateError = err?.response?.status === 409 && err?.response?.data?.applicationId;
      if (isDuplicateError) {
        msg = `${msg} (ID aplikasi: ${err.response.data.applicationId}).`;
      }
      setError(msg);
      if (isDuplicateError) {
        setDuplicateDialog({ open: true, message: msg });
      } else {
        setDuplicateDialog({ open: false, message: '' });
      }
      console.error('Submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fungsi untuk menambahkan saudara kandung
  const addSibling = () => {
    setForm(prev => ({
      ...prev,
      siblings: [...(prev.siblings || []), { relation: '', age: '', education: '', job: '' }]
    }));
  };

  // Fungsi untuk menghapus saudara kandung
  const removeSibling = (index) => {
    setForm(prev => ({
      ...prev,
      siblings: (prev.siblings || []).filter((_, i) => i !== index)
    }));
  };

  // Fungsi untuk menambahkan anak
  const addChild = () => {
    setForm(prev => ({
      ...prev,
      children: [...(prev.children || []), { relation: '', age: '', education: '', job: '' }]
    }));
  };

  // Fungsi untuk menghapus anak
  const removeChild = (index) => {
    setForm(prev => ({
      ...prev,
      children: (prev.children || []).filter((_, i) => i !== index)
    }));
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <Step1DataPribadi 
          key="step-0"
          form={form} 
          setForm={setForm} 
          addSibling={addSibling}
          removeSibling={removeSibling}
          addChild={addChild}
          removeChild={removeChild}
        />;
      case 1:
        return <Step2Pendidikan 
          key="step-1"
          form={form} 
          setForm={setForm} 
        />;
      case 2:
        return <Step3PengalamanKerja 
          key="step-2"
          form={form} 
          setForm={setForm} 
        />;
      case 3:
        return <Step4Bahasa 
          key="step-3"
          form={form} 
          setForm={setForm} 
        />;
      case 4:
        return <Step5Gaji 
          key="step-4"
          form={form} 
          setForm={setForm} 
        />;
      case 5:
        return <Step6PertanyaanTambahan 
          key="step-5"
          form={form} 
          setForm={setForm} 
        />;
      case 6:
        return <Step8Dokumen 
          key="step-6"
          form={form} 
          setForm={setForm} 
        />;
      case 7:
        return <Step7Review 
          key="step-7"
          form={form} 
          onEdit={handleEdit} 
          onSubmit={handleSubmit} 
        />;
      default:
        return 'Unknown step';
    }
  };

  // Effect untuk membersihkan error saat form berubah
  useEffect(() => {
    if (Object.keys(form).length > 0) {
      setError(''); // Clear error message saat form berubah
    }
  }, [form]);

  // Effect untuk menghapus class error saat user mulai mengisi field dan saat step berubah
  useEffect(() => {
    // Hapus semua class error saat step berubah
    document.querySelectorAll('.error-field').forEach(el => {
      el.classList.remove('error-field');
    });
    
    const handleInputStart = (e) => {
      if (e.target.classList.contains('error-field')) {
        e.target.classList.remove('error-field');
      }
    };
    
    // Tambahkan event listener ke semua input, textarea, dan select
    const inputs = document.querySelectorAll('input, textarea, select');
    inputs.forEach(input => {
      input.addEventListener('input', handleInputStart);
      input.addEventListener('change', handleInputStart);
    });
    
    return () => {
      // Cleanup event listeners
      inputs.forEach(input => {
        input.removeEventListener('input', handleInputStart);
        input.removeEventListener('change', handleInputStart);
      });
    };
  }, [activeStep]); // Re-run saat step berubah

  // Fungsi validasi yang memberikan pesan error detail
  // Fungsi untuk menandai field yang error dengan border merah
  const highlightErrorFields = () => {
    // Hapus semua class error terlebih dahulu
    document.querySelectorAll('.error-field').forEach(el => {
      el.classList.remove('error-field');
    });
    
    // Tandai field yang kosong dengan class error
    const validation = getStepValidation(activeStep);
    if (!validation.isValid) {
      const fieldMappings = {
        // Step 1 - Data Pribadi
        'Nama Lengkap': 'fullName',
        'Email': 'email',
        'No. Telepon': 'phone',
        'Tempat Lahir': 'birthPlace',
        'Tanggal Lahir': 'birthDate',
        'Kewarganegaraan': 'nationality',
        'Agama': 'religion',
        'Status Pernikahan': 'maritalStatus',
        'Jenis Kelamin': 'gender',
        'No. KTP': 'ktpNumber',
        'Tinggal Bersama': 'livingArrangement',
        'Alamat KTP': 'addressKtp',
        'Alamat Domisili': 'addressCurrent',
        'Hobby': 'hobby',
        'Sifat-sifat Positif': 'selfPositivePoints',
        'Kelemahan': 'selfWeaknessPoints',
        'Inovasi/Perubahan': 'innovationDescription',
        
        // Step 3 - Bahasa
        'Nama Bahasa': 'language',
        'Level Kemampuan Menulis': 'writingLevel',
        'Level Kemampuan Lisan': 'speakingLevel',
        
        // Step 4 - Gaji
        'Gaji Minimum yang Diharapkan': 'minimumSalary',
        'Gaji Ideal yang Diinginkan': 'expectedSalary',
        
        // Step 5 - Pertanyaan Tambahan
        'Alasan ingin bergabung dengan perusahaan': 'whyJoinCompany',
        'Kapan bisa mulai bekerja': 'whenCanStart',
        'Pengetahuan tentang perusahaan': 'companyKnowledge',
        'Sumber informasi lowongan': 'jobSource',
        'Expectasi gaji': 'idealSalary'
      };
      
      // Parse missing fields berdasarkan step
      let missingFields = [];
      if (activeStep === 0) {
        // Step 1 - Format bullet points
        missingFields = validation.message.match(/• ([^\n]+)/g)?.map(field => field.replace('• ', '')) || [];
      } else if (activeStep === 3) {
        // Step 3 - Bahasa, highlight semua field yang kosong
        missingFields = ['Nama Bahasa', 'Level Kemampuan Menulis', 'Level Kemampuan Lisan'];
      } else if (activeStep === 4) {
        // Step 4 - Gaji, cek field mana yang kosong
        if (!form.minimumSalary || form.minimumSalary.trim() === '') {
          missingFields.push('Gaji Minimum yang Diharapkan');
        }
        if (!form.expectedSalary || form.expectedSalary.trim() === '') {
          missingFields.push('Gaji Ideal yang Diinginkan');
        }
      } else if (activeStep === 5) {
        // Step 5 - Pertanyaan Tambahan
        missingFields = validation.message.match(/• ([^\n]+)/g)?.map(field => field.replace('• ', '')) || [];
      }
      
      // Special handling untuk Step 3 (Bahasa) - highlight semua field kosong
      if (activeStep === 3) {
        highlightLanguageFields();
      } else {
        // Normal handling untuk step lainnya
        missingFields.forEach(fieldName => {
          const fieldId = fieldMappings[fieldName];
          if (fieldId) {
            const input = document.querySelector(`input[name="${fieldId}"], textarea[name="${fieldId}"], select[name="${fieldId}"]`);
            if (input) {
              input.classList.add('error-field');
              // Fokus ke field pertama yang error
              if (missingFields.indexOf(fieldName) === 0) {
                input.focus();
                input.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }
          }
        });
      }
    }
  };

  // Fungsi khusus untuk menandai field bahasa yang kosong
  const highlightLanguageFields = () => {
    const languages = form.languages || [];
    
    // Cari semua baris bahasa di dalam tabel
    const languageRows = document.querySelectorAll('.MuiGrid-root');
    let firstEmptyField = null;
    
    languageRows.forEach((row, rowIndex) => {
      // Cek apakah baris ini berisi field bahasa (memiliki select elements)
      const selects = row.querySelectorAll('select');
      const textareas = row.querySelectorAll('textarea');
      
      if (selects.length > 0) {
        // Baris bahasa ditemukan
        const languageIndex = Math.floor(rowIndex / 6); // Perkiraan index bahasa
        
        if (languageIndex < languages.length) {
          const language = languages[languageIndex];
          
          // Cek field Nama Bahasa (select pertama)
          if (selects[0] && (!language || !language.language || language.language.trim() === '')) {
            selects[0].classList.add('error-field');
            if (!firstEmptyField) firstEmptyField = selects[0];
          }
          
          // Cek field Level Menulis (select kedua)
          if (selects[1] && (!language || !language.written || language.written.trim() === '')) {
            selects[1].classList.add('error-field');
            if (!firstEmptyField) firstEmptyField = selects[1];
          }
          
          // Cek field Level Lisan (select ketiga)
          if (selects[2] && (!language || !language.oral || language.oral.trim() === '')) {
            selects[2].classList.add('error-field');
            if (!firstEmptyField) firstEmptyField = selects[2];
          }
          
          // Cek field Keterangan (textarea)
          if (textareas[0] && textareas.length > 0) {
            // Keterangan optional, tidak perlu ditandai
          }
        }
      }
    });
    
    // Focus ke field pertama yang kosong
    if (firstEmptyField) {
      firstEmptyField.focus();
      firstEmptyField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const getStepValidation = (step) => {
    const missingFields = [];
    
    switch (step) {
      case 0: // Data Pribadi
        // Data Dasar
        if (!form.fullName || form.fullName.trim() === '') missingFields.push('Nama Lengkap');
        if (!form.email || form.email.trim() === '') missingFields.push('Email');
        if (!form.phone || form.phone.trim() === '') missingFields.push('No. Telepon');
        
        // Data Identitas
        if (!form.birthPlace || form.birthPlace.trim() === '') missingFields.push('Tempat Lahir');
        if (!form.birthDate || form.birthDate.trim() === '') missingFields.push('Tanggal Lahir');
        if (!form.nationality || form.nationality.trim() === '') missingFields.push('Kewarganegaraan');
        if (!form.religion || form.religion.trim() === '') missingFields.push('Agama');
        if (!form.maritalStatus || form.maritalStatus.trim() === '') missingFields.push('Status Pernikahan');
        if (!form.gender || form.gender.trim() === '') missingFields.push('Jenis Kelamin');
        
        // Data Alamat
        if (!form.ktpNumber || form.ktpNumber.trim() === '') missingFields.push('No. KTP');
        if (!form.livingArrangement || form.livingArrangement.trim() === '') missingFields.push('Tinggal Bersama');
        if (!form.addressKtp || form.addressKtp.trim() === '') missingFields.push('Alamat KTP');
        if (!form.addressCurrent || form.addressCurrent.trim() === '') missingFields.push('Alamat Domisili');
        
        // Data Karakteristik Pribadi
        if (!form.hobby || form.hobby.trim() === '') missingFields.push('Hobby');
        if (!form.selfPositivePoints || form.selfPositivePoints.trim() === '') missingFields.push('Sifat-sifat Positif');
        if (!form.selfWeaknessPoints || form.selfWeaknessPoints.trim() === '') missingFields.push('Kelemahan');
        if (!form.innovationDescription || form.innovationDescription.trim() === '') missingFields.push('Inovasi/Perubahan');
        
        if (missingFields.length > 0) {
          return {
            isValid: false,
            message: `⚠️ Data Pribadi belum lengkap!\n\nField yang harus diisi:\n• ${missingFields.join('\n• ')}\n\nSilakan lengkapi semua field yang bertanda bintang (*) merah.`
          };
        }
        return { isValid: true, message: '' };
        
      case 1: // Pendidikan
        const education = form.education || [];
        if (!education.length || !education[0] || !education[0].schoolName || education[0].schoolName.trim() === '') {
          return {
            isValid: false,
            message: `⚠️ Data Pendidikan belum lengkap!\n\nAnda harus mengisi minimal satu riwayat pendidikan dengan:\n• Nama Sekolah/Institusi\n• Periode/Tahun\n• Jurusan (jika ada)\n\nSilakan lengkapi data pendidikan Anda.`
          };
        }
        return { isValid: true, message: '' };
        
      case 2: // Pengalaman Kerja
        // Step ini optional, selalu valid
        return { isValid: true, message: '' };
        
      case 3: // Bahasa
        const languages = form.languages || [];
        if (!languages.length || !languages[0] || !languages[0].language || languages[0].language.trim() === '') {
          return {
            isValid: false,
            message: `⚠️ Data Kemampuan Bahasa belum lengkap!\n\nAnda harus mengisi minimal satu kemampuan bahasa dengan:\n• Nama Bahasa\n• Level Kemampuan Menulis\n• Level Kemampuan Lisan\n\nSilakan lengkapi data kemampuan bahasa Anda.`
          };
        }
        return { isValid: true, message: '' };
        
      case 4: // Gaji & Tunjangan
        if ((!form.minimumSalary || form.minimumSalary.trim() === '') && 
            (!form.expectedSalary || form.expectedSalary.trim() === '')) {
          return {
            isValid: false,
            message: `⚠️ Data Gaji & Tunjangan belum lengkap!\n\nAnda harus mengisi minimal salah satu dari:\n• Gaji Minimum yang Diharapkan\n• Gaji Ideal yang Diinginkan\n\nSilakan lengkapi ekspektasi gaji Anda.`
          };
        }
        return { isValid: true, message: '' };
        
      case 5: // Pertanyaan Tambahan
        if (!form.whyJoinCompany || form.whyJoinCompany.trim() === '') missingFields.push('Alasan ingin bergabung dengan perusahaan');
        if (!form.whenCanStart || form.whenCanStart.trim() === '') missingFields.push('Kapan bisa mulai bekerja');
        if (!form.companyKnowledge || form.companyKnowledge.trim() === '') missingFields.push('Pengetahuan tentang perusahaan');
        
        if (missingFields.length > 0) {
          return {
            isValid: false,
            message: `⚠️ Pertanyaan Tambahan belum lengkap!\n\nField yang harus diisi:\n• ${missingFields.join('\n• ')}\n\nSilakan jawab semua pertanyaan yang wajib diisi.`
          };
        }
        return { isValid: true, message: '' };
        
      case 6: // Upload Dokumen
        const documents = form.documents || [];
        const requiredDocTypes = ['pas_foto', 'ijazah', 'cv'];
        const missingDocuments = [];
        
        // Check if each required document type exists
        requiredDocTypes.forEach(docType => {
          const hasDocument = documents.some(doc => doc.type === docType);
          if (!hasDocument) {
            switch(docType) {
              case 'pas_foto':
                missingDocuments.push('Pas Foto');
                break;
              case 'ijazah':
                missingDocuments.push('Ijazah Terakhir');
                break;
              case 'cv':
                missingDocuments.push('Curriculum Vitae (CV)');
                break;
              default:
                missingDocuments.push(docType);
                break;
            }
          }
        });
        
        if (missingDocuments.length > 0) {
          return {
            isValid: false,
            message: `⚠️ Upload Dokumen belum lengkap!\n\nDokumen wajib yang harus diupload:\n• ${missingDocuments.join('\n• ')}\n\nSilakan upload semua dokumen yang diperlukan sebelum melanjutkan ke tahap review.`
          };
        }
        return { isValid: true, message: '' };
        
      case 7: // Review
        return { isValid: true, message: '' }; // Always valid
        
      default:
        return { isValid: false, message: 'Step tidak dikenali.' };
    }
  };

  const isStepComplete = (step) => {
    return getStepValidation(step).isValid;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <style jsx>{`
        .error-field {
          border: 2px solid #f44336 !important;
          background-color: #fff8f8 !important;
          animation: shake 0.5s ease-in-out;
        }
        
        .error-field:focus {
          border-color: #d32f2f !important;
          box-shadow: 0 0 0 2px rgba(211, 47, 47, 0.2) !important;
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        
        .error-message {
          animation: slideDown 0.3s ease-out;
        }
        
        @keyframes slideDown {
          from { 
            opacity: 0; 
            transform: translateY(-20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
      `}</style>
      
      {/* Error Alert - Moved to top */}
      {error && (
        <Alert 
          severity="error" 
          className="error-message"
          sx={{ 
            mb: 3,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <AlertTitle sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
            Peringatan Validasi
          </AlertTitle>
          <Box 
            component="pre" 
            sx={{ 
              fontFamily: 'inherit',
              fontSize: '0.95rem',
              lineHeight: 1.6,
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word'
            }}
          >
            {error}
          </Box>
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            backgroundColor: '#ffffff',
            border: '2px solid #1976d2',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)'
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#1976d2',
                mb: 2,
                fontSize: '2rem'
              }}
            >
              Form Pendaftaran Recruitment
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666',
                fontSize: '1.1rem',
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Silakan lengkapi data diri Anda dengan benar dan lengkap untuk proses pendaftaran.
            </Typography>
          </Box>

          {/* Navigation - Stepper */}
          <RecruitmentNavigation 
            activeStep={activeStep}
            steps={steps}
            onStepClick={handleStepClick}
          />

          {/* Step Content */}
          <Box sx={{ mt: 4 }}>
            {getStepContent(activeStep)}
          </Box>

          {/* Navigation Buttons */}
          {activeStep !== steps.length - 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={activeStep === 0}
                sx={{ minWidth: 100 }}
              >
                Sebelumnya
              </Button>
              
              <Typography variant="body2" color="text.secondary">
                Step {activeStep + 1} dari {steps.length}
              </Typography>
              
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isStepComplete(activeStep)}
                sx={{ minWidth: 100 }}
              >
                {activeStep === steps.length - 2 ? 'Review' : 'Selanjutnya'}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={duplicateDialog.open}
        onClose={handleDuplicateDialogClose}
        aria-labelledby="duplicate-warning-title"
        aria-describedby="duplicate-warning-description"
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown
      >
        <DialogTitle id="duplicate-warning-title" sx={{ fontWeight: 'bold' }}>
          Peringatan Validasi
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="duplicate-warning-description"
            sx={{ whiteSpace: 'pre-line', color: 'text.primary' }}
          >
            {duplicateDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={closeDuplicateDialog} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          <AlertTitle>Berhasil!</AlertTitle>
          Data pendaftaran Anda telah berhasil dikirim.
        </Alert>
      </Snackbar>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">
              Mengirim data...
            </Typography>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default RecruitmentForm;
